import { formatNumber } from '@angular/common';
import {
    Class,
    DivIcon,
    DomEvent,
    DomUtil,
    Handler,
    LatLng,
    LatLngBounds,
    Layer,
    LayerGroup,
    LeafletEvent,
    LeafletMouseEvent,
    Map,
    Marker,
    PathOptions,
    Point,
    Polygon,
    Polyline,
    PolylineOptions,
    Rectangle,
    Util,
} from 'leaflet';
import { fromEvent, merge, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { pointInPolygon, Polygon as GeoPolygon, polygonIntersect, Position } from './models/geometry';
import { ACRE_TO_HA, Area, Distance, M2_IN_HA } from './models/units';


const readableArea = (area: number) => {
    const a = new Area(area);
    const i = a.getQuantity(true);
    const m = a.getQuantity(false);
    const iValue = formatNumber(i.value, 'en_US', '1.2-2');
    const mValue = formatNumber(m.value, 'en_US', '1.2-2');
    return `<strong>Area:</strong> ${iValue}&nbsp;${i.unit} / ${mValue}&nbsp;${m.unit}`;
};

const readableDistance = (distance: number) => {
    const d = new Distance(distance);
    const i = d.getQuantity(true);
    const m = d.getQuantity(false);
    const iValue = formatNumber(i.value, 'en_US', '1.2-2');
    const mValue = formatNumber(m.value, 'en_US', '1.2-2');
    return `<strong>Distance:</strong> ${iValue}&nbsp;${i.unit} / ${mValue}&nbsp;${m.unit}`;
};


// Ported from the OpenLayers implementation.
// See https://github.com/openlayers/openlayers/blob/master/lib/OpenLayers/Geometry/LinearRing.js#L270
export const geodesicArea = (latLngs: LatLng[]) => {
    let area = 0;
    const d2r = Math.PI / 180;

    if (latLngs.length > 2) {
        for (let i = 0; i < latLngs.length; i++) {
            const p1 = latLngs[i];
            const p2 = latLngs[(i + 1) % latLngs.length];
            area += ((p2.lng - p1.lng) * d2r) * (2 + Math.sin(p1.lat * d2r) + Math.sin(p2.lat * d2r));
        }
        area = area * 6378137.0 * 6378137.0 / 2.0;
    }

    return Math.abs(area);
};


export class Tooltip extends Class {

    private map: Map;

    private visible = false;

    private container: HTMLElement;

    constructor(map: Map) {
        super();
        this.map = map;
        this.container = DomUtil.create('div', 'draw-tooltip', map.getPanes().tooltipPane);
        this.map.on('mouseover', this.onMouseOver, this);
        this.map.on('mouseout', this.onMouseOut, this);
    }

    updateContent(labelText: { text?: string; subtext?: string }) {
        if (!this.container) {
            return;
        }
        labelText.subtext = labelText.subtext || '';

        this.container.innerHTML = `
            ${
                labelText.subtext.length > 0
                ? `<span class="subtext">${labelText.subtext}</span><br>`
                : ''
            }
            <span>${labelText.text}</span>
        `;

        if (!labelText.text && !labelText.subtext) {
            this.visible = false;
            this.container.style.visibility = 'hidden';
        } else {
            this.visible = true;
            this.container.style.visibility = 'inherit';
        }
    }

    updatePosition(latlng: LatLng) {
        if (!this.visible) {
            return;
        }
        const pos = this.map.latLngToLayerPoint(latlng);
        DomUtil.setPosition(this.container, pos);
    }

    showAsError() {
        DomUtil.addClass(this.container, 'error');
    }

    removeError() {
        DomUtil.removeClass(this.container, 'error');
    }

    private onMouseOut() {
        this.container.style.visibility = 'hidden';
    }

    private onMouseOver() {
        this.container.style.visibility = this.visible ? 'inherit' : 'hidden';
    }

    destroy() {
        this.map.off('mouseover', this.onMouseOver, this);
        this.map.off('mouseout', this.onMouseOut, this);
        if (this.container) {
            this.map.getPanes().tooltipPane.removeChild(this.container);
        }
    }
}


abstract class DrawFeature extends Handler {

    protected map: Map;

    protected container: HTMLElement;

    protected tooltip: Tooltip;

    protected currentLatLng: LatLng;

    constructor(map: Map) {
        super(map);
        this.map = map;
        this.container = map.getContainer();
    }

    enable() {
        if (this.enabled()) {
            return this;
        }
        super.enable();
        this.map.fire('draw:start');
        return this;
    }

    disable() {
        if (!this.enabled()) {
            return this;
        }
        this.map.fire('draw:stop');
        super.disable();
        return this;
    }

    addHooks() {
        this.map.getContainer().focus();
        this.tooltip = new Tooltip(this.map);
        this.map.on('mousemove', this.onMouseMove, this);
        DomEvent.on(this.container, 'keyup', this.cancelDrawing, this);
    }

    removeHooks() {
        this.tooltip.destroy();
        this.map.off('mousemove', this.onMouseMove, this);
        DomEvent.off(this.container, 'keyup', this.cancelDrawing, this);
    }

    protected onMouseMove(e: LeafletEvent) {
        const { latlng } = e as LeafletMouseEvent;
        this.currentLatLng = latlng;
        this.tooltip.updatePosition(latlng);
    }

    protected cancelDrawing(e: Event) {
        if ((e as KeyboardEvent).key === 'Escape') {
            this.map.fire('draw:cancel');
            this.disable();
        }
    }
}


type DrawPolylineOptions = {
    showLength: boolean;
    areaLimit: number;
    icon: DivIcon;
    editIcon: DivIcon;
    errorColor: string;
    shapeOptions: PathOptions;
    drawStartText?: string;
    drawContinueText?: string;
    drawEndText?: string;
}


const defaultOptions: DrawPolylineOptions = {
    showLength: true,
    areaLimit: 100 * ACRE_TO_HA * M2_IN_HA,
    icon: new DivIcon({
        iconSize: [8, 8],
        className: 'draw-marker'
    }),
    editIcon: new DivIcon({
        iconSize: [20, 20],
        className: 'draw-edit-marker'
    }),
    errorColor: '#F42',
    shapeOptions: {
        color: '#08F',
        stroke: true,
        weight: 4,
        opacity: 0.5,
        fill: true,
        fillOpacity: 0.2,
    },
};


export class DrawRectangle extends DrawFeature {

    protected options: DrawPolylineOptions;

    protected shape: Rectangle;

    protected draggable: boolean;

    protected isDrawing = false;

    protected startLatLng: LatLng;

    protected removed = new Subject<void>();

    constructor(map: Map, options?: Partial<DrawPolylineOptions>) {
        super(map);
        this.options = {
            drawStartText: 'Click and drag to draw a rectangle.',
            drawEndText: 'Release drag to finish drawing.',
            ...defaultOptions,
            ...options,
            shapeOptions: {
                ...defaultOptions.shapeOptions,
                ...options?.shapeOptions,
            },
        };
    }

    addHooks() {
        super.addHooks();
        this.draggable = this.map.dragging.enabled();

        if (this.draggable) {
            this.map.dragging.disable();
        }

        DomUtil.addClass(this.container, 'drawing');
        this.tooltip.updateContent({ text: this.options.drawStartText });

        this.map.on('mousedown', this.onMouseDown, this);

        const touchStart = fromEvent<TouchEvent>(this.container, 'touchstart', { passive: false });
        const touchMove = fromEvent<TouchEvent>(document, 'touchmove');
        const touchEnd = merge(fromEvent<TouchEvent>(document, 'touchend'), fromEvent(document, 'touchcancel'));

        const touchToLatLng = (touch: Touch) => {
            const { clientX, clientY } = touch;
            const { left, top } = this.container.getBoundingClientRect();
            return this.map.containerPointToLatLng([clientX - left, clientY - top]);
        };

        touchStart.pipe(
            switchMap(event => {
                event.preventDefault();
                if (event.touches.length !== 1) {
                    return [];
                }
                this.map.fire('mousedown', { latlng: touchToLatLng(event.touches[0]) });
                return touchMove.pipe(
                    tap(event => {
                        this.map.fire('mousemove', { latlng: touchToLatLng(event.touches[0]) });
                    }),
                    takeUntil(touchEnd.pipe(tap(() => {
                        this.onMouseUp();
                    }))),
                );
            }),
            takeUntil(this.removed),
        ).subscribe();
    }

    removeHooks() {
        if (this.draggable) {
            this.map.dragging.enable();
        }

        if (this.shape) {
            this.map.removeLayer(this.shape);
        }

        DomUtil.removeClass(this.container, 'drawing');

        this.map.off('mousedown', this.onMouseDown, this);
        DomEvent.off(document.documentElement, 'mouseup', this.onMouseUp, this);

        this.removed.next();

        super.removeHooks();
    }

    protected onMouseDown(e: LeafletMouseEvent) {
        this.isDrawing = true;
        this.startLatLng = e.latlng;
        this.tooltip.updateContent({ text: this.options.drawEndText });
        DomEvent.on(document.documentElement, 'mouseup', this.onMouseUp, this);
    }

    protected onMouseMove(e: LeafletMouseEvent) {
        super.onMouseMove(e);
        if (this.isDrawing) {
            this.drawShape();
        }
    }

    protected onMouseUp() {
        this.isDrawing = false;
        if (this.shape) {
            this.fireCreatedEvent();
            this.disable();
        }
    }

    protected drawShape() {
        const bounds = new LatLngBounds(this.startLatLng, this.currentLatLng);
        if (!this.shape) {
            this.shape = new Rectangle(bounds, this.options.shapeOptions);
            this.map.addLayer(this.shape);
        } else {
            this.shape.setBounds(bounds);
        }

        if (this.options.areaLimit) {
            const area = geodesicArea(this.shape.getLatLngs()[0] as LatLng[]);
            if (area > this.options.areaLimit) {
                this.shape.setStyle({ ...this.options.shapeOptions, color: this.options.errorColor });
                this.tooltip.showAsError();
                this.tooltip.updateContent({
                    text: '<strong>Error:</strong> Rectangle you have drawn is too large. Please select area within 100 acres.'
                });
            } else {
                this.shape.setStyle(this.options.shapeOptions);
                this.tooltip.removeError();
                this.tooltip.updateContent({ text: this.options.drawEndText, subtext: readableArea(area) });
            }
        }
    }

    protected fireCreatedEvent() {
        this.map.fire('draw:created', { layer: new Rectangle(this.shape.getBounds(), this.options.shapeOptions) });
    }
}


class DrawPolyline extends DrawFeature {

    protected options: DrawPolylineOptions;

    protected markers: Marker[];

    protected markerGroup: LayerGroup;

    protected poly: Polyline;

    protected mouseMarker: Marker;

    protected guide: Polyline;

    protected errorShown: boolean;

    protected measurementRunningTotal = 0;

    protected hideErrorTimeout: unknown;

    constructor(map: Map, options?: Partial<DrawPolylineOptions>) {
        super(map);
        this.options = {
            drawStartText: 'Click to start drawing a line.',
            drawContinueText: 'Click to continue drawing the line.',
            drawEndText: 'Click last point to finish this line.',
            ...defaultOptions,
            ...options,
            shapeOptions: {
                ...defaultOptions.shapeOptions,
                ...options?.shapeOptions,
            },
        };
    }

    addHooks() {
        super.addHooks();
        this.markers = [];

        this.markerGroup = new LayerGroup();
        this.map.addLayer(this.markerGroup);

        this.poly = new Polyline([], this.options.shapeOptions);
        this.guide = new Polyline([], { className: 'draw-guide' });

        this.tooltip.updateContent(this.getTooltipText());

        // Make a transparent marker that will used to catch click events. These click
        // events will create the vertices. We need to do this so we can ensure that
        // we can create vertices over other map layers (markers, vector layers). We
        // also do not want to trigger any click handlers of objects we are clicking on
        // while drawing.
        this.mouseMarker = new Marker(this.map.getCenter(), {
            icon: new DivIcon({ className: 'draw-mouse-marker', iconAnchor: [20, 20], iconSize: [40, 40] }),
            opacity: 0,
            zIndexOffset: 2000,
        });

        this.mouseMarker.on('mousedown', this.onMouseDown, this);
        this.mouseMarker.addTo(this.map);

        this.map.on('mousemove', this.onMouseMove, this);
        this.map.on('mouseup', this.onMouseUp, this);
        this.map.on('zoomlevelschange', this.onZoomEnd, this);
        this.map.on('zoomend', this.onZoomEnd, this);
    }

    removeHooks() {
        this.clearHideErrorTimeout();
        this.cleanUpShape();

        this.map.removeLayer(this.markerGroup);
        this.map.removeLayer(this.poly);
        this.map.removeLayer(this.guide);

        this.mouseMarker.off();
        this.map.removeLayer(this.mouseMarker);

        this.map.off('mouseup', this.onMouseUp, this);
        this.map.off('mousemove', this.onMouseMove, this);
        this.map.off('zoomlevelschange', this.onZoomEnd, this);
        this.map.off('zoomend', this.onZoomEnd, this);

        return super.removeHooks();
    }

    protected addVertex(latLng: LatLng) {
        /*
        if (this.markers.length >= 2 && this.poly['newLatLngIntersects'](latLng)) {
            this.showErrorTooltip();
            return;
        }
        */
        if (this.errorShown) {
            this.hideErrorTooltip();
        }

        this.markers.push(this.createMarker(latLng));

        this.poly.addLatLng(latLng);

        if (this.poly.getLatLngs().length === 1) {
            this.map.addLayer(this.guide);
        }

        if (this.poly.getLatLngs().length === 2) {
            this.map.addLayer(this.poly);
        }

        this.vertexChanged(latLng, true);
    }

    protected finishShape() {
        // const latLngs = this.poly.getLatLngs();
        // const intersects = this.poly['newLatLngIntersects'](latLngs[latLngs.length - 1]);
        const intersects = false; // this.poly['newLatLngIntersects'](latLngs[latLngs.length - 1]);

        if (intersects || !this.shapeIsValid()) {
            this.showErrorTooltip();
            return;
        }

        this.fireCreatedEvent();
        this.disable();
    }

    protected shapeIsValid() {
        return true;
    }

    protected onZoomEnd() {
        this.updateGuide();
    }

    protected onMouseMove(e: LeafletMouseEvent) {
        super.onMouseMove(e);
        this.updateGuide();
        this.mouseMarker.setLatLng(this.currentLatLng);
        this.updateTooltip();
    }

    protected vertexChanged(latlng: LatLng, added: boolean) {
        this.updateFinishHandler();
        this.updateRunningMeasure(latlng, added);
        this.updateTooltip();
    }

    protected mouseDownOrigin: Point | null = null;

    protected onMouseDown(e: LeafletMouseEvent) {
        this.onMouseMove(e);
        const { clientX, clientY } = e.originalEvent;
        this.startPoint(clientX, clientY);
    }

    protected startPoint(clientX: number, clientY: number) {
        this.mouseDownOrigin = new Point(clientX, clientY);
    }

    protected onMouseUp(e: LeafletMouseEvent) {
        const { clientX, clientY } = e.originalEvent;
        this.endPoint(clientX, clientY, e);
    }

    protected endPoint(clientX: number, clientY: number, e: LeafletMouseEvent) {
        if (this.mouseDownOrigin) {
            const dragCheckDistance = new Point(clientX, clientY).distanceTo(this.mouseDownOrigin);
            if (Math.abs(dragCheckDistance) < 9 * (window.devicePixelRatio || 1)) {
                this.addVertex(e.latlng);
            }
        }
        this.mouseDownOrigin = null;
    }

    protected updateFinishHandler() {
        // The last marker should have a click handler to close the polyline
        if (this.markers.length > 1) {
            this.markers[this.markers.length - 1].on('click', this.finishShape, this);
        }

        // Remove the old marker click handler (as only the last point should close the polyline)
        if (this.markers.length > 2) {
            this.markers[this.markers.length - 2].off('click', this.finishShape, this);
        }
    }

    protected createMarker(latLng: LatLng) {
        const marker = new Marker(latLng, {
            icon: this.options.icon,
            zIndexOffset: 2100,
        });
        this.markerGroup.addLayer(marker);
        return marker;
    }

    protected updateGuide() {
        if (this.markers.length > 0) {
            const start = this.markers[this.markers.length - 1].getLatLng();
            const end = this.currentLatLng;
            this.guide.setLatLngs([start, end]);
        } else {
            this.guide.setLatLngs([]);
        }
    }

    protected updateTooltip(latLng?: LatLng) {
        const text = this.getTooltipText();

        if (latLng) {
            this.tooltip.updatePosition(latLng);
        }

        if (!this.errorShown) {
            this.tooltip.updateContent(text);
        }
    }

    protected getTooltipText() {
        if (this.markers.length === 0) {
            return { text: this.options.drawStartText };
        }

        const showLength = this.options.showLength;
        const distanceStr = showLength ? this.getMeasurementString() : '';
        if (this.markers.length === 1) {
            return { text: this.options.drawContinueText, subtext: distanceStr };
        }

        return { text: this.options.drawEndText, subtext: distanceStr };
    }

    protected updateRunningMeasure(latlng: LatLng, added: boolean) {
        if (this.markers.length === 1) {
            this.measurementRunningTotal = 0;
        } else {
            const previousMarkerIndex = this.markers.length - (added ? 2 : 1);
            const distance = this.map.distance(latlng, this.markers[previousMarkerIndex].getLatLng());
            this.measurementRunningTotal += distance * (added ? 1 : -1);
        }
    }

    protected getMeasurementString() {
        const currentLatLng = this.currentLatLng;
        const previousLatLng = this.markers[this.markers.length - 1].getLatLng();
        const mouseDistance = previousLatLng && currentLatLng ? this.map.distance(currentLatLng, previousLatLng) : 0;
        return readableDistance(this.measurementRunningTotal + mouseDistance);
    }

    protected showErrorTooltip() {
        this.errorShown = true;

        this.tooltip.updateContent({ text: '<strong>Error:</strong> shape edges cannot cross!' });
        this.tooltip.showAsError();

        this.guide.setStyle({ color: this.options.errorColor });
        this.poly.setStyle({ color: this.options.errorColor });

        this.clearHideErrorTimeout();
        this.hideErrorTimeout = setTimeout(Util.bind(this.hideErrorTooltip, this), 2500);
    }

    protected hideErrorTooltip() {
        this.errorShown = false;
        this.clearHideErrorTimeout();

        this.tooltip.removeError();
        this.tooltip.updateContent(this.getTooltipText());

        this.guide.setStyle({ color: this.options.shapeOptions.color });
        this.poly.setStyle({ color: this.options.shapeOptions.color });
    }

    protected clearHideErrorTimeout() {
        clearTimeout(this.hideErrorTimeout as number);
    }

    protected cleanUpShape() {
        if (this.markers.length > 1) {
            this.markers[this.markers.length - 1].off('click', this.finishShape, this);
        }
    }

    protected fireCreatedEvent() {
        const poly = new Polyline(this.poly.getLatLngs() as LatLng[][], this.options.shapeOptions);
        this.map.fire('draw:created', { layer: poly });
    }
}


export class DrawPolygon extends DrawPolyline {

    protected area = 0;

    constructor(map: Map, options?: Partial<DrawPolylineOptions>) {
        super(map, {
            drawStartText: 'Click to start drawing a shape.',
            drawContinueText: 'Click to continue drawing the shape.',
            drawEndText: 'Click first point to close this shape.',
            ...options,
        });
    }

    protected updateFinishHandler() {
        // The first marker should have a click handler to close the polygon
        if (this.markers.length === 1) {
            this.markers[0].on('click', this.finishShape, this);
            this.markers[0].setIcon(this.options.editIcon);
            const element = this.markers[0].getElement();
            if (element) {
                DomUtil.addClass(element, 'close-shape');
            }
        }

        // Add and update the double click handler
        if (this.markers.length > 2) {
            this.markers[this.markers.length - 1].on('dblclick', this.finishShape, this);
            // Only need to remove handler if has been added before
            if (this.markers.length > 3) {
                this.markers[this.markers.length - 2].off('dblclick', this.finishShape, this);
            }
        }
    }

    protected getMeasurementString() {
        if (!this.area && !this.options.showLength) {
            return '';
        }
        let measurementString = '';
        if (this.options.showLength) {
            measurementString = super.getMeasurementString();
        }
        if (this.area) {
            measurementString += '<br>' + readableArea(this.area);
        }
        return measurementString;
    }

    protected shapeIsValid() {
        return this.markers.length >= 3;
    }

    protected vertexChanged(latlng: LatLng, added: boolean) {
        const latLngs = this.poly.getLatLngs() as LatLng[];
        this.area = geodesicArea(latLngs);
        super.vertexChanged(latlng, added);
        if (this.options.areaLimit > 0) {
            if (this.area > this.options.areaLimit) {
                this.poly.setStyle({ color: this.options.errorColor });
                this.tooltip.showAsError();
                this.tooltip.updateContent({
                    text: '<strong>Error:</strong> Polygon you have drawn is too large. Please select area within 100 acres.'
                });
                this.errorShown = true;
            } else {
                this.poly.setStyle({ color: this.options.shapeOptions.color });
                this.errorShown = false;
            }
        }
    }

    protected cleanUpShape() {
        if (this.markers.length > 0) {
            this.markers[0].off('click', this.finishShape, this);

            if (this.markers.length > 2) {
                this.markers[this.markers.length - 1].off('dblclick', this.finishShape, this);
            }
        }
    }

    protected fireCreatedEvent() {
        const poly = new Polygon(this.poly.getLatLngs() as LatLng[][], this.options.shapeOptions);
        this.map.fire('draw:created', { layer: poly });
    }
}


function save() {
    const featureIds: string[] = [];
    // @ts-ignore
    for (const layer of this.map._deletedLayers.getLayers()) {
        if (layer.feature) {
            featureIds.push(layer.feature.id);
        } else if (typeof layer.getLayers === 'function') {
            for (const l of layer.getLayers()) {
                if (l.feature) {
                    featureIds.push(l.feature.id);
                }
            }
        }
    }
    // @ts-ignore
    this.map.fire('draw:deleted', { layers: featureIds });
    // @ts-ignore
    this.map._deletedLayers.clearLayers();
}


function revertLayers() {
    // Iterate of the deleted layers and add them back into the featureGroup
    // @ts-ignore
    this.map._deletedLayers.eachLayer((layer: Layer) => {
        // @ts-ignore
        this.deletableLayers.addLayer(layer);
        // layer.fire('revert-deleted', { layer: layer });
    });
    // @ts-ignore
    this.map._deletedLayers.clearLayers();
}


export class DrawDeletingPoint extends DrawFeature {

    protected deletableLayers: LayerGroup;

    constructor(map: Map, options: { featureGroup: LayerGroup }) {
        super(map);
        this.deletableLayers = options.featureGroup;
    }

    addHooks() {
        super.addHooks();
        // @ts-ignore
        if (typeof this.map['_deletedLayers'] === 'undefined') {
            // @ts-ignore
            this.map['_deletedLayers'] = new LayerGroup();
        }
        DomUtil.addClass(this.container, 'drawing');
        this.tooltip.updateContent({ text: 'Click on a property to remove.' });
        this.deletableLayers.on('click', this.removeLayer, this);
    }

    removeHooks() {
        DomUtil.removeClass(this.container, 'drawing');
        this.deletableLayers.off('click', this.removeLayer, this);
        super.removeHooks();
    }

    clearLayers() {
        this.deletableLayers.clearLayers();
    }

    revertLayers() {
        revertLayers.call(this);
    }

    save() {
        save.call(this);
    }

    protected removeLayer(e: LeafletMouseEvent) {
        const point = [e.latlng.lng, e.latlng.lat] as const;
        this.deletableLayers.eachLayer(layer => {
            // @ts-ignore
            const geometry = layer['feature'].geometry;
            const polygons = geometry.type === 'MultiPolygon' ? geometry.coordinates : [geometry.coordinates];
            if (polygons.some((poly: Position[][]) => pointInPolygon(point, poly))) {
                this.deletableLayers.removeLayer(layer);
                // @ts-ignore
                this.map['_deletedLayers'].addLayer(layer);
                layer.fire('deleted');
            }
        });
    }
}


function handleFireCreatedEvent(latLngs: LatLng[]) {
    const coordinates: LatLng[] = [...latLngs, latLngs[0]];
    const polygon: GeoPolygon = {
        type: 'Polygon',
        coordinates: [coordinates.map(latLng => [latLng.lng, latLng.lat])],
    };
    // @ts-ignore
    this.deletableLayers.eachLayer((layer: Layer) => {
        // @ts-ignore
        if (polygonIntersect(layer['feature'].geometry, polygon)) {
            // @ts-ignore
            this.deletableLayers.removeLayer(layer);
            // @ts-ignore
            this.map._deletedLayers.addLayer(layer);
            layer.fire('deleted');
        }
    });
}


export class DrawDeletingRectangle extends DrawRectangle {

    protected deletableLayers: LayerGroup;

    constructor(map: Map, options: Partial<PolylineOptions> & { featureGroup: LayerGroup }) {
        super(map, { areaLimit: 0, shapeOptions: options });
        this.deletableLayers = options.featureGroup;
    }

    addHooks() {
        super.addHooks();
        // @ts-ignore
        if (typeof this.map['_deletedLayers'] === 'undefined') {
            // @ts-ignore
            this.map['_deletedLayers'] = new LayerGroup();
        }
    }

    clearLayers() {
        this.deletableLayers.clearLayers();
    }

    revertLayers() {
        revertLayers.call(this);
    }

    save() {
        save.call(this);
    }

    protected fireCreatedEvent() {
        handleFireCreatedEvent.call(this, this.shape.getLatLngs()[0] as LatLng[]);
        super.fireCreatedEvent();
    }
}


export class DrawDeletingPolygon extends DrawPolygon {

    protected deletableLayers: LayerGroup;

    constructor(map: Map, options: Partial<PolylineOptions> & { featureGroup: LayerGroup }) {
        super(map, { showLength: false, areaLimit: 0, shapeOptions: options });
        this.deletableLayers = options.featureGroup;
    }

    addHooks() {
        super.addHooks();
        // @ts-ignore
        if (typeof this.map['_deletedLayers'] === 'undefined') {
            // @ts-ignore
            this.map['_deletedLayers'] = new LayerGroup();
        }
    }

    clearLayers() {
        this.deletableLayers.clearLayers();
    }

    revertLayers() {
        revertLayers.call(this);
    }

    save() {
        save.call(this);
    }

    protected fireCreatedEvent() {
        handleFireCreatedEvent.call(this, this.poly.getLatLngs() as LatLng[]);
        super.fireCreatedEvent();
    }
}


declare module 'leaflet' {

    interface Evented {
        on(text: 'edit', fn: (polygon: EditablePolygon) => void): this;
    }
}


export class EditablePolygon extends Polygon {

    private markersGroup = new LayerGroup();

    private markers: Marker[] = [];

    options: PolylineOptions & { editIcon: DivIcon };

    private get latLngs() {
        return this.getLatLngs()[0] as LatLng[];
    }

    constructor(latLngs: LatLng[], options?: Partial<PolylineOptions & { editIcon: DivIcon }>) {
        super(latLngs, { ...options, editIcon: defaultOptions.editIcon } as PolylineOptions);
    }

    onAdd(map: Map) {
        super.onAdd(map);
        map.addLayer(this.markersGroup);

        const latLngs = this.latLngs;
        for (let i = 0; i < latLngs.length; i++) {
            this.markers.push(this.createMarker(latLngs[i], i));
        }
        for (let i = 0, j = latLngs.length - 1; i < latLngs.length; j = i++) {
            this.createMiddleMarker(this.markers[j], this.markers[i]);
            this.updatePrevNext(this.markers[j], this.markers[i]);
        }

        return this;
    }

    onRemove(map: Map) {
        this.markers.forEach(marker => marker.off());
        map.removeLayer(this.markersGroup);
        super.onRemove(map);
        return this;
    }

    private fireEdit() {
        this.fire('edit', this);
    }

    private createMarker(latLng: LatLng, index: number) {
        const marker = new Marker(latLng, {
            icon: this.options.editIcon,
            draggable: true,
            zIndexOffset: 2100,
        });
        marker.on('drag', this.onMarkerDrag, this);
        marker.on('dragend', this.onMarkerDragEnd, this);
        marker.on('dblclick', this.onMarkerDblClick, this);
        // @ts-ignore
        marker['_index'] = index;
        this.markersGroup.addLayer(marker);
        return marker;
    }

    private onMarkerDrag(e: LeafletEvent) {
        const marker = e.target;
        this.latLngs.splice(marker._index, 1, marker.getLatLng());
        if (marker._middleLeft) {
            marker._middleLeft.setLatLng(this.getMiddleLatLng(marker._prev, marker));
        }
        if (marker._middleRight) {
            marker._middleRight.setLatLng(this.getMiddleLatLng(marker, marker._next));
        }
        this.redraw();
    }

    private onMarkerDragEnd() {
        this.fireEdit();
    }

    private onMarkerDblClick(e: LeafletMouseEvent) {
        const marker = e.target;
        if (this.latLngs.length < 4) {
            return;
        }

        const i = marker._index;
        this.markersGroup.removeLayer(marker);
        this.markers.splice(i, 1);
        this.latLngs.splice(i, 1);
        this.updateIndexes(i, -1);

        this.updatePrevNext(marker._prev, marker._next);

        if (marker._middleLeft) {
            this.markersGroup.removeLayer(marker._middleLeft);
        }
        if (marker._middleRight) {
            this.markersGroup.removeLayer(marker._middleRight);
        }

        if (marker._prev && marker._next) {
            this.createMiddleMarker(marker._prev, marker._next);
        } else if (!marker._prev) {
            marker._next._middleLeft = null;
        } else if (!marker._next) {
            marker._prev._middleRight = null;
        }

        this.redraw();
        this.fireEdit();
    }

    private createMiddleMarker(marker1: Marker, marker2: Marker) {
        const latLng = this.getMiddleLatLng(marker1, marker2);
        const marker = new Marker(latLng, {
            icon: this.options.editIcon,
            draggable: true,
            zIndexOffset: 2100,
        });
        const noop = () => {};
        marker.setOpacity(0.5);

        marker.on('drag', this.onMarkerDrag, this);
        marker.on('dblclick', noop);

        this.markersGroup.addLayer(marker);

        // @ts-ignore
        marker1['_middleRight'] = marker;
        // @ts-ignore
        marker2['_middleLeft'] = marker;

        const onDragStart = () => {
            // @ts-ignore
            const i = marker2['_index'];

            this.latLngs.splice(i, 0, marker.getLatLng());
            this.markers.splice(i, 0, marker);

            marker.setOpacity(1);

            this.updateIndexes(i, 1);
            // @ts-ignore
            marker['_index'] = i;
            // @ts-ignore
            marker2['_index']++;
            this.updatePrevNext(marker1, marker);
            this.updatePrevNext(marker, marker2);
        };

        const onDragEnd = () => {
            marker.off('dragstart', onDragStart, this);
            marker.off('dragend', onDragEnd, this);
            marker.off('dblclick', noop);
            marker.on('dblclick', this.onMarkerDblClick, this);
            marker.on('dragend', this.onMarkerDragEnd, this);
            this.createMiddleMarker(marker1, marker);
            this.createMiddleMarker(marker, marker2);
            this.onMarkerDragEnd();
        };

        marker.on('dragstart', onDragStart, this);
        marker.on('dragend', onDragEnd, this);

        this.markersGroup.addLayer(marker);
    }

    private getMiddleLatLng(marker1: Marker, marker2: Marker) {
        const p1 = this._map.project(marker1.getLatLng());
        const p2 = this._map.project(marker2.getLatLng());
        return this._map.unproject(p1.add(p2).divideBy(2));
    }

    private updateIndexes(index: number, delta: number) {
        this.markersGroup.eachLayer(marker => {
            // @ts-ignore
            if (marker['_index'] > index) {
                // @ts-ignore
                marker['_index'] += delta;
            }
        });
    }

    private updatePrevNext(marker1: Marker, marker2: Marker) {
        if (marker1) {
            // @ts-ignore
            marker1['_next'] = marker2;
        }
        if (marker2) {
            // @ts-ignore
            marker2['_prev'] = marker1;
        }
    }
}


export class DrawEditablePolygon extends DrawPolygon {

    constructor(map: Map, options?: Partial<DrawPolylineOptions>) {
        super(map, { ...options, areaLimit: 0 });
    }
}
